import React, { useState, useRef } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { salesCyclesApi } from '../../services/api';

interface SalesRepDropdownProps {
  selectedValue: string | null;
  onChange: (value: string | null) => void;
  isFetching?: boolean;
  selectedTeam?: string | null;
  className?: string;
}

const SalesRepDropdown: React.FC<SalesRepDropdownProps> = ({ 
  selectedValue, 
  onChange, 
  isFetching = false,
  selectedTeam,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [salesReps, setSalesReps] = useState<{hs_owner_id: string, rep_name: string, team_name: string}[]>([]);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  React.useEffect(() => {
    const fetchSalesReps = async () => {
      try {
        const reps = await salesCyclesApi.getSalesReps(selectedTeam || undefined);
        setSalesReps(reps);
      } catch (error) {
        console.error('Error fetching sales reps:', error);
      }
    };
    fetchSalesReps();
  }, [selectedTeam]);

  const handleSelectValue = (value: string) => {
    onChange(value || null);
    setIsOpen(false);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setShowTopShadow(target.scrollTop > 0);
    setShowBottomShadow(target.scrollTop < target.scrollHeight - target.clientHeight);
  };

  // Filter sales reps by selected team
  const filteredSalesReps = selectedTeam 
    ? salesReps.filter(rep => rep.team_name === selectedTeam)
    : salesReps;

  const selectedRep = filteredSalesReps.find(rep => rep.rep_name === selectedValue);
  const isFiltered = selectedValue && selectedValue !== '';

  return (
    <div className={`relative ${className}`}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer text-sm min-h-[2.5rem] flex items-center justify-between shadow-sm font-inter transition-all duration-200 ${
          isFiltered
            ? 'bg-gray-50 dark:bg-gray-600 border border-purple-200 dark:border-purple-700'
            : 'bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
        }`}
      >
        <div className="flex items-center flex-1 mr-2">
          <span className="truncate">
            {selectedRep ? selectedRep.rep_name : (selectedTeam ? `All Reps in ${selectedTeam}` : 'All Sales Reps')}
          </span>
        </div>
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''} ${
          isFiltered ? 'text-purple-500' : 'text-gray-500 dark:text-gray-400'
        }`} />
      </div>

      {isOpen && (
        <div className="absolute z-[9999] right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg min-w-[280px]">
          {/* Top scroll shadow indicator */}
          {showTopShadow && (
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-b from-gray-100 dark:from-gray-700 to-transparent pointer-events-none z-10 rounded-t-xl" />
          )}
          
          {/* Bottom scroll shadow indicator */}
          {showBottomShadow && (
            <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-t from-gray-100 dark:from-gray-700 to-transparent pointer-events-none z-10 rounded-b-xl" />
          )}
          
          <div 
            ref={dropdownRef}
            className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 transition-colors"
            onScroll={handleScroll}
          >
            <div
              key="all"
              onClick={() => handleSelectValue('')}
              className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm font-inter flex items-center justify-between text-gray-700 dark:text-gray-300"
            >
              <span>{selectedTeam ? `All Reps in ${selectedTeam}` : 'All Sales Reps'}</span>
              {!selectedValue && <Check className="h-4 w-4 text-purple-600 flex-shrink-0" />}
            </div>
            {filteredSalesReps.map((rep) => {
              const isSelected = selectedValue === rep.rep_name;
              return (
                <div
                  key={rep.hs_owner_id}
                  onClick={() => handleSelectValue(rep.rep_name)}
                  className={`px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm font-inter flex items-center justify-between ${
                    isSelected ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <span className="truncate block">{rep.rep_name}</span>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{rep.team_name}</div>
                  </div>
                  {isSelected && <Check className="h-4 w-4 text-purple-600 flex-shrink-0" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isOpen && (
        <div 
          className="fixed inset-0 z-[9998]" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default SalesRepDropdown;
